<vs-app-page
  *vsLet="distributor$ | async as distributor"
  class="h-fit pb-4"
  [config]="{
    title: distributor?.name || 'Distributor',
    description: 'Review and manage the details and associated members for this distributor.',
    icon: 'package'
  }"
>
  <vs-breadcrumbs *ngIf="!hideBreadcrumbs" slot="breadcrumbs">
    <vs-breadcrumbs-item [config]="{ label: 'Distributors', routerLink: '..' }"></vs-breadcrumbs-item>
    <vs-breadcrumbs-item
      *ngIf="distributor?.parent"
      [config]="{ label: distributor!.parent!.name, routerLink: ['../', distributor!.parent!.id] }"
    ></vs-breadcrumbs-item>
    <vs-breadcrumbs-item [config]="{ label: distributor?.name}"></vs-breadcrumbs-item>
  </vs-breadcrumbs>

  <vs-badge
    slot="title-end"
    [theme]="distributor?.status === activated ? 'success' : distributor?.status === deactivated ? 'info' : 'default'"
  >
    {{ distributor?.status | titlecase }}
  </vs-badge>

  <div
    *ngIf="distributor?.status !== archived"
    slot="header-end"
    class="flex gap-2 justify-between lg:max-w-min w-full"
  >
    <ng-container *vsLet="checkoutLink$ | async as checkoutLink">
      <div class="flex flex-row">
        <vs-input-group *ngIf="(canUpdateStatus$ | async) && (canEditDistributor$ | async); else noEdit " class="ml-2">
          <!-- Add permissions check -->
          <button
            vs-button
            appearance="outline"
            class="flex text-base !text-gray-700"
            (click)="distributor?.status === activated ? deactivateDistributorDialog.openDialog() : reactivateDistributorDialog.openDialog()"
          >
            {{ distributor?.status === activated ? "Deactivate" : "Reactivate" }}
          </button>

          <button
            vs-button
            appearance="outline"
            class="flex text-base !text-gray-700"
            (click)="archiveDistributorDialog.openDialog()"
          >
            Archive
          </button>
          <button
            *ngIf="distributor?.status === activated"
            vs-icon-button
            appearance="outline"
            [vsMenuTriggerFor]="menu"
            class="gap-2 flex-row"
          >
            <vs-icon name="chevron-down"></vs-icon>
          </button>
        </vs-input-group>
        <ng-template #noEdit>
          <button
            *ngIf="distributor?.status === activated"
            vs-button
            appearance="outline"
            [vsMenuTriggerFor]="menu"
            class="gap-2 flex-row"
          >
            Links
            <vs-icon name="chevron-down"></vs-icon>
          </button>
        </ng-template>
      </div>

      <ng-template #menu>
        <vs-menu class="mt-1" *ngIf="canViewDistributor$ | async">
          <button vs-menu-item class="!p-0 [&>div]:!rounded-none" (triggered)="createLinkDialog.openDialog()">
            <vs-icon name="link-external-01" slot="start"></vs-icon>
            <span>Onboarding Links</span>
          </button>

          <button
            vs-menu-item
            class="!p-0 [&>div]:!rounded-none"
            [disabled]="!checkoutLink"
            (triggered)="copyCheckoukLink(checkoutLink)"
          >
            <vs-icon name="copy-01" slot="start"></vs-icon>
            <span>Copy Checkout Link</span>
          </button>
        </vs-menu>
      </ng-template>
    </ng-container>
  </div>

  <vs-tabs #tabs theme="primary" (tabChanged)="onTabChanged()">
    <vs-tab [config]="{ label: 'Overview' }">
      <div class="max-w-7xl mx-auto">
        <ng-container *vsLet="pinnedNotes$ | async as pinnedNotes">
          <!-- `loadingPinnedNotes$` subject ensures the transition works properly by first rendering the content with 0fr (see setTimeout in TS) -->
          <div
            *ngIf="pinnedNotes?.length"
            class="transition-all grid -m-2"
            [style.grid-template-rows]="(loadingPinnedNotes$ | async) === false ? '1fr' : '0fr'"
          >
            <!-- Combination of negative margin and pasdding used to show the shadow of the card even with the overflow hidden -->
            <!-- PS: 8px chosen arbitrarily to ensure most shadow sizes are visible -->
            <div class="overflow-hidden p-2 grid gap-4">
              <ng-container *ngFor="let note of pinnedNotes">
                <vs-card class="px-6 py-4">
                  <div class="overflow-hidden">
                    <header class="mb-3 flex flex-wrap gap-x-4 gap-y-2 text-gray-700 text-sm select-none">
                      <div class="flex items-center">
                        <vs-icon name="pin-02" class="text-primary-500"></vs-icon>
                      </div>

                      <div class="flex items-center gap-1 overflow-hidden">
                        <vs-icon name="user-01"></vs-icon>
                        <span class="truncate" *ngIf="note.createdBy">{{ note.createdBy.displayName }}</span>
                        <span class="truncate" *ngIf="!note.createdBy" class="italic">System</span>
                      </div>

                      <div class="flex items-center gap-1 overflow-hidden">
                        <vs-icon name="calendar-date"></vs-icon>
                        <span class="truncate">{{ note.created | date : 'mediumDate' }}</span>
                      </div>

                      <div class="flex items-center gap-1 overflow-hidden">
                        <vs-icon name="clock"></vs-icon>
                        <span class="truncate">{{ note.created | date : 'shortTime' }}</span>
                      </div>
                    </header>

                    <section class="max-h-96 overflow-auto scrollbar-thin">
                      <div [innerHTML]="note.content | safe : 'html'"></div>
                    </section>
                  </div>
                </vs-card>
              </ng-container>
            </div>
          </div>
        </ng-container>

        <ws-distributor-overview-page
          #overview
          theme="default"
          [distributor]="distributor ?? undefined"
          [onSettings]="hideBreadcrumbs"
          (distributorChanged)="refreshDistributor($event)"
        ></ws-distributor-overview-page>
      </div>
    </vs-tab>

    <vs-tab *ngIf="(canViewStaff$ | async) === true" [config]="{ label: 'Team' }">
      <div class="max-w-7xl mx-auto">
        <ws-distributor-team-page theme="default" [distributor]="distributor ?? undefined"></ws-distributor-team-page>
      </div>
    </vs-tab>

    <vs-tab [config]="{ label: 'Notes' }">
      <div class="max-w-7xl mx-auto">
        <ws-distributor-notes-page [distributorId]="distributor?.id ?? null"></ws-distributor-notes-page>
      </div>
    </vs-tab>

    <vs-tab *ngIf="(canViewDocuments$ | async) === true" [config]="{ label: 'Documents' }">
      <div class="max-w-7xl mx-auto">
        <ws-distributor-documents-page [distributor]="distributor"></ws-distributor-documents-page>
      </div>
    </vs-tab>

    <vs-tab *ngIf="(canManageAccountInfo$ | async) === true" [config]="{ label: 'Account' }">
      <div class="max-w-7xl mx-auto">
        <ws-distributor-account-page [distributor]="distributor"></ws-distributor-account-page>
      </div>
    </vs-tab>

    <vs-tab *ngIf="(canViewCommission$ | async) === true" [config]="{ label: 'Commission' }">
      <div class="max-w-7xl mx-auto">
        <ws-distributor-commission-page [distributor]="distributor"></ws-distributor-commission-page>
      </div>
    </vs-tab>

    <vs-tab *ngIf="(canViewWarranties$ | async) === true" [config]="{ label: 'Unfunded Warranties' }">
      <div class="max-w-7xl mx-auto">
        <ws-distributor-unfunded-warranties-page
          [distributorId]="distributor?.id ?? null"
          [distributorAddress]="distributor?.address ?? null"
        ></ws-distributor-unfunded-warranties-page>
      </div>
    </vs-tab>

    <vs-tab *ngIf="(canViewWarranties$ | async) === true" [config]="{ label: 'Payment History' }">
      <div class="max-w-7xl mx-auto">
        <ws-distributor-payment-history-page
          [distributorId]="distributor?.id ?? null"
        ></ws-distributor-payment-history-page>
      </div>
    </vs-tab>

    <vs-tab *ngIf="(canViewLedgerStatement$ | async) === true" [config]="{ label: 'Ledger Statement' }">
      <div class="max-w-7xl mx-auto">
        <ws-distributor-ledger-statement-page
          [distributorId]="distributor?.id ?? null"
          [distributorPermissionKey]="distributor?.permissionKey ?? null"
        ></ws-distributor-ledger-statement-page>
      </div>
    </vs-tab>
    <!-- <vs-tab *ngIf="distributor?.status !== archived && (sftpEnabled$ | async) === true" [config]="{ label: 'SFTP' }">
      <ws-distributor-sftp-page
        theme="default"
        [distributor]="distributor"
        (goToDistEmail)="tabs.switchTo(0); overview.openEditDialog()"
      ></ws-distributor-sftp-page>
    </vs-tab> -->
  </vs-tabs>

  <ws-archive-distributor-dialog
    #archiveDistributorDialog
    [distributor]="distributor"
    (closed)="refreshDistributor($event)"
  >
  </ws-archive-distributor-dialog>

  <ws-deactivate-distributor-dialog
    #deactivateDistributorDialog
    [distributor]="distributor"
    (closed)="refreshDistributor($event)"
  ></ws-deactivate-distributor-dialog>

  <ws-reactivate-distributor-dialog
    #reactivateDistributorDialog
    [distributor]="distributor"
    (closed)="refreshDistributor($event)"
  ></ws-reactivate-distributor-dialog>

  <ws-create-distributor-onboarding-link-dialog
    #createLinkDialog
    [distributor]="distributor"
    [selectedPage]="manageLinks"
    [showRelevantLinks]="true"
  >
  </ws-create-distributor-onboarding-link-dialog>
</vs-app-page>
